import { createSession } from "@thekrystalship/auth-client";
import { REGISTRY_KEY, entryNode, homeConn, originOfHost } from "./config.js";
import { clientIdFor, discoverProvider, originOf } from "./oidc.js";
import { observe } from "./connectivity.js";
import { readProvider, writeProvider } from "./provider.js";
import { createStore } from "@thekrystalship/krystal/lib/store";
import { hostsStore } from "./stores.js";

// sessionStore.js — one session, for the whole cluster.
//
// The cluster's sign-in provider (an OpenID Connect provider, on its own origin, outside the cluster)
// mints the session, and this panel is a client of it through `@thekrystalship/auth-client`: a round
// trip to the provider's pages signs somebody in, and the package keeps the session alive (renewal
// ahead of expiry, a replayed refused call, an outage held through, tabs agreeing) without being seen.
// Every member accepts the session by verifying the provider's signature against the keys it publishes.
// So there is one token here and no member ever issues this browser a credential. The token lists
// the actions its holder may perform, and each member's `/me/access` answer reports them
// (`stores/access.js`). A role change reaches the person when the token renews.
//
// ── What this module adds to the package ────────────────────────────────────
//   • Discovery: the provider is whatever the page's origin, the entry node, or the person's typed
//     address names (`oidc.js`, `provider.js`), and the client id is the page origin's host.
//   • The record the shell reads: `{ status, reason, account, open }`. A recoverable lapse is never a
//     status of its own: a session the package holds as `stale` is `live` here, and nothing about
//     renewal is drawn. Only a session nothing can recover is `ended`, or `denied` when the account
//     may not use the cluster.
//   • Open hosts: a host run with auth switched off answers `/me` to anybody, so its session carries
//     no token (`open`).
//   • Per-node acceptance: one token goes to every member, and `nodes` records which members are
//     currently honouring it. A member's 401 on a fresh session is a fact about that member.
//
// Status:
//   none          nothing held; the gate is showing
//   bootstrapping the stored session or an open-host probe is being settled
//   live          a bearer is held (or the host runs open) — calls allowed
//   ended         the session is over and nothing can bring it back; the gate offers sign-in
//   denied        the cluster grants this account nothing — terminal, never auto-retried

  // Where the provider sends a browser back to with its code. A path of its own rather than a hash
  // route, because the provider matches the address exactly and the router's hash is the panel's.
  const LANDING_PATH = "/signed-in";

  const store = createStore({ session: null, nodes: {} });

  let open = false;      // a host run with auth switched off: live, tokenless
  let openAccount = "unknown";
  let probing = false;   // the open-host probe is in flight
  let session = null;    // the package's session for the provider known now
  let sessionIssuer = "";
  let unsubscribeSession = null;
  let tokenExpiry = null;

  const rec = () => store.getState().session;
  const statusOf = () => (rec() ? rec().status : "none");
  const isLive = () => statusOf() === "live";
  const isDenied = () => statusOf() === "denied";
  // Where the account stands: active, pending (awaiting approval), or unknown. Null with no session.
  // The provider hands a session only to an active account, so a held one is active.
  const accountOf = () => { const r = rec(); return r ? (r.account || "unknown") : null; };

  // The record the shell reads, from what is held now.
  function recordOf() {
    if (open) return { status: "live", reason: null, account: openAccount, userId: null, open: true };
    if (probing) return { status: "bootstrapping", reason: null, account: null, userId: null, open: false };
    const snap = session ? session.getSnapshot() : null;
    if (!snap || snap.status === "none") return null;
    if (snap.status === "restoring") return { status: "bootstrapping", reason: null, account: null, userId: null, open: false };
    if (snap.status === "ended") {
      return { status: snap.reason === "denied" ? "denied" : "ended", reason: snap.reason, account: null, userId: null, open: false };
    }
    const account = snap.account && snap.account.status ? snap.account.status : "active";
    return { status: "live", reason: null, account, userId: snap.account && snap.account.id ? snap.account.id : null, open: false };
  }

  function sync() {
    const next = recordOf();
    const prev = rec();
    const snap = session ? session.getSnapshot() : null;
    // A renewed token is a new session as far as the members are concerned: nobody has refused it yet.
    const expiry = snap && snap.status === "live" ? snap.expiresAt : null;
    const renewed = expiry != null && expiry !== tokenExpiry;
    const rolled = renewed && tokenExpiry != null;
    tokenExpiry = expiry;
    const same = (!prev && !next) || (prev && next && prev.status === next.status && prev.reason === next.reason
      && prev.account === next.account && prev.userId === next.userId && prev.open === next.open);
    if (same && !renewed) return;
    store.setState(s => ({ ...s, session: same ? s.session : next, nodes: renewed ? {} : s.nodes }));
    if (rolled) renewalListeners.forEach((fn) => { try { fn(); } catch { /* one listener must not stop the rest */ } });
  }

  // Told each time the token is replaced by a renewed one, which is when a role change reaches the
  // person. Returns the way to stop listening.
  const renewalListeners = new Set();
  function onRenewal(fn) { renewalListeners.add(fn); return () => renewalListeners.delete(fn); }

  // ---- the provider -------------------------------------------------------
  // One package session per issuer, built when first needed. The issuer is the stored fact provider.js
  // holds; a different one is a different cluster, and the session is rebuilt for it.
  function sessionFor() {
    const p = readProvider();
    if (!p) return null;
    if (!session || sessionIssuer !== p.issuer) {
      if (unsubscribeSession) unsubscribeSession();
      const clientId = clientIdFor(window.location.origin) || "";
      session = createSession({
        resolve: async () => ({ issuer: p.issuer, clientId }),
        redirectPath: LANDING_PATH,
        postSignOutPath: "/",
      });
      sessionIssuer = p.issuer;
      unsubscribeSession = session.subscribe(sync);
    }
    return session;
  }

  // Where the cluster's accounts are administered: the provider's own origin, which serves the account
  // API beside the pages. Empty with no provider known.
  const providerOrigin = () => { const p = readProvider(); return p ? originOf(p.issuer) : ""; };

  // Record which provider this panel signs in at, as `discoverProvider` found it. Another cluster's
  // provider is another cluster, and the nodes remembered for the first are not its nodes.
  function setProvider(found) {
    const before = readProvider();
    const next = found && found.issuer ? { issuer: found.issuer, via: found.via || null } : null;
    if (before && next && before.issuer !== next.issuer) writeRegistry([]);
    writeProvider(next);
    if (readProvider()) sessionFor();
    else if (session) {
      unsubscribeSession();
      unsubscribeSession = null;
      session = null;
      sessionIssuer = "";
    }
    sync();
  }

  // A stored provider is the issuer as it was found, and a provider's issuer can be set again since. Once a
  // load, after the shell is up, the provider is asked again: the address that named it first, then the
  // entry node and the page's origin, the way the gate finds one. An
  // issuer the first answer names differently replaces the stored one, so the account page opens at the
  // right address now and the next load signs in there. Nothing answering changes nothing.
  async function confirmProvider({ fetchImpl = fetch } = {}) {
    const p = readProvider();
    if (!p) return;
    for (const address of [p.via, entryNode(), window.location.origin]) {
      if (!address) continue;
      const found = await discoverProvider(address, { fetchImpl, signal: AbortSignal.timeout(10000) });
      if (!found.ok) continue;
      if (found.issuer !== p.issuer) writeProvider({ issuer: found.issuer, via: p.via });
      return;
    }
  }

  // The account page, where everything about a person's own credentials is changed.
  const accountPage = () => { const o = providerOrigin(); return o ? o + "/account" : ""; };

  // One of the provider's admin pages — accounts, roles, permissions, catalog, assignments,
  // applications — with its parameters filled in. Who may do what is administered there, never here.
  // Empty with no provider known.
  const adminPage = (page, params) => {
    const o = providerOrigin();
    if (!o) return "";
    const query = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v != null && v !== "")).toString();
    return o + "/admin/#/" + page + (query ? "?" + query : "");
  };

  // ---- the nodes this browser drives --------------------------------------
  // Addresses, never identity: one session is presented to all of them. The entry node is configured
  // and its roster names the rest (`stores/cluster.js`); every node the panel has driven is remembered
  // here, so a load can start from any of them when the entry node is down.
  function readRegistry() {
    try { return JSON.parse(localStorage.getItem(REGISTRY_KEY) || "[]"); } catch { return []; }
  }
  function writeRegistry(list) {
    try { localStorage.setItem(REGISTRY_KEY, JSON.stringify(list)); } catch { /* private mode */ }
  }
  function register(host) {
    const url = host.url || originOfHost(host.id);
    if (!url || !/^https?:\/\//i.test(url)) return;
    const list = readRegistry().filter(h => h.id !== host.id);
    list.push({ id: host.id, url, name: host.name || host.label || host.id });
    writeRegistry(list);
  }

  // ---- per-node acceptance ------------------------------------------------
  // Who is currently honouring the one session. Written by the seam, read by the surfaces that name
  // a node. `refusing` is a fact about that member: a 401 that survived a renewal, `unverified_here`,
  // because it could not check the token at all. It clears the moment the member answers, and a
  // renewed session clears every one of them, since none of them were about it.
  function nodeAccepts(id) { const n = store.getState().nodes[id]; return !n || n.accepts === "ok"; }
  function nodeRefusal(id) { const n = store.getState().nodes[id]; return n && n.accepts === "refusing" ? n : null; }
  function markNode(id, accepts, reason) {
    if (!id) return;
    store.setState(s => {
      const cur = s.nodes[id];
      if (cur && cur.accepts === accepts && cur.reason === reason) return s;
      return { ...s, nodes: { ...s.nodes, [id]: { accepts, reason: reason || null } } };
    });
  }
  function forgetNode(id) {
    if (!id) return;
    store.setState(s => {
      if (!(id in s.nodes)) return s;
      const nodes = { ...s.nodes };
      delete nodes[id];
      return { ...s, nodes };
    });
  }

  // ---- sign in ------------------------------------------------------------
  // Send the browser to the provider to sign in, carrying where it should come back to (a hash route,
  // or nothing). Resolves false when there is no provider to send it to or it could not be asked;
  // otherwise the page is leaving.
  async function signIn(back) {
    const s = sessionFor();
    if (!s) return false;
    return s.signIn({ returnTo: "/" + (back || "") });
  }

  // Whether this page is the provider sending a browser back with its code.
  const isLanding = () => typeof window !== "undefined" && window.location.pathname === LANDING_PATH;

  // Settle the session before anything mounts: on the landing path the package exchanges the code and
  // answers the route the browser left from (`"/#/…"`, or null); anywhere else it loads the stored
  // session and starts renewing it.
  async function restore() {
    const s = sessionFor();
    if (!s) return null;
    return s.restore();
  }

  // ---- open hosts (auth disabled) -----------------------------------------
  // A host running with auth switched off answers `/me` to an anonymous caller. That is a whole
  // deployment's posture rather than a per-node one, so it is resolved once against the node serving
  // the panel and the session runs tokenless.
  async function bootstrapOpen() {
    const conn = homeConn();
    if (!conn) return "none";
    probing = true;
    sync();
    try {
      const res = await fetch(conn.url + "/api/v1/me", { headers: { Accept: "application/json" } });
      if (!res.ok) return "none";
      const me = await res.json();
      open = true;
      openAccount = (me && me.status) || "unknown";
      return "live";
    } catch {
      return "none";
    } finally {
      probing = false;
      sync();
    }
  }

  // ---- authorize (ensure a live session) ----------------------------------
  // The seam's entry point. Resolves the status the session settles at: `live`, `ended`, `denied`, or
  // `none` when nobody has signed in — which is the gate's business rather than something to heal here.
  async function authorize() {
    if (open) return "live";
    const s = sessionFor();
    if (s) {
      if (s.getSnapshot().status === "restoring") await s.restore();
      const st = statusOf();
      if (st === "live" || st === "ended" || st === "denied") return st;
      return "none";
    }
    if (readProvider()) return "none";
    return bootstrapOpen();
  }

  // A token valid for at least the package's renew-ahead window, renewed here if it is not; with
  // `{ force: true }` it is renewed now (a token the other side refused). Null for an open host.
  // Rejects when there is no session.
  async function bearer(options) {
    if (open) return null;
    const s = sessionFor();
    if (!s) throw Object.assign(new Error("There is no session."), { code: "none" });
    return s.fresh(options);
  }

  // `fetch` with the session's bearer, for a call that may be sent twice: a 401 renews once and replays
  // once. An open host's calls go out as they are. Every call the panel makes to a node passes here, so
  // this is where a call to the entry node reports whether the node answered (`connectivity.js`).
  function sessionFetch(input, init) {
    if (open) return observe(input, fetch(input, init));
    const s = sessionFor();
    if (!s) return Promise.reject(Object.assign(new Error("There is no session."), { code: "none" }));
    return observe(input, s.authorizedFetch(input, init));
  }

  // A token fresh enough to spend, renewed if it is not. Resolves the status that is left.
  async function rotate() {
    try { await bearer(); } catch { /* the status says what is left */ }
    return statusOf();
  }

  // The token, or null once there is none to hand out.
  const tokenOf = () => bearer().catch(() => null);

  // Sign out of the cluster, at the provider. Its end-session endpoint ends the provider's own session
  // and every session minted under it, so no renewal follows; the access token in this tab lapses
  // within its short lifetime. It sends the browser back here. Resolves true while the page is leaving
  // for it; false when there was no session to end there, and the local drop was all there was to do.
  async function signOut() {
    // The chat keeps the person's conversations in this browser; they leave with the person. The chat
    // package is loaded on demand, so a panel that never opened the assistant does not pull it in here.
    try { (await import("@thekrystalship/krystal-chat")).forgetConversations(); } catch { /* nothing kept to forget */ }
    const s = session;
    const held = !open && !!s && ["live", "stale"].includes(s.getSnapshot().status);
    open = false;
    store.setState({ session: null, nodes: {} });
    if (!s) return false;
    await s.signOut();
    return held;
  }

  // Every node forgotten as well — the panel drops back to having no cluster to drive.
  function forgetHosts() {
    writeRegistry([]);
    store.setState(s => ({ ...s, nodes: {} }));
    hostsStore.setState(s => ({ ...s, list: [] }));
  }

  store.statusOf = statusOf;
  store.isDenied = isDenied;
  store.isLive = isLive;
  store.accountOf = accountOf;
  store.tokenOf = tokenOf;
  store.bearer = bearer;
  store.fetch = sessionFetch;
  store.rotate = rotate;
  store.authorize = authorize;
  store.register = register;
  store.readRegistry = readRegistry;
  store.signIn = signIn;
  store.isLanding = isLanding;
  store.restore = restore;
  store.signOut = signOut;
  store.forgetHosts = forgetHosts;
  store.nodeAccepts = nodeAccepts;
  store.nodeRefusal = nodeRefusal;
  store.markNode = markNode;
  store.forgetNode = forgetNode;
  store.providerOrigin = providerOrigin;
  store.accountPage = accountPage;
  store.adminPage = adminPage;
  store.setProvider = setProvider;
  store.confirmProvider = confirmProvider;
  store.provider = readProvider;
  store.onRenewal = onRenewal;

  const sessionStore = store;

// The cluster session as something a call can be authorized BY: the package's own `fresh` and
// `authorizedFetch`, answering for an open host as well, which holds no token and sends nothing.
const clusterSession = { fresh: bearer, authorizedFetch: sessionFetch };

export { clusterSession, sessionStore };
